import { StrategyDetailsPageListDocumentRecordsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { STRATEGY_DETAILS_PAGE_DELETE_DOCUMENT_RECORD_MUTATION } from '../queries';

interface DeleteUpdateProps {
  isOpen: boolean;
  currentRow: StrategyDetailsPageListDocumentRecordsQuery['listDocumentRecords']['records'][number] | null;
  listDocumentRecordsRefetch: () => void;
  onClose: () => void;
}

const DeleteUpdate = ({ isOpen, currentRow, listDocumentRecordsRefetch, onClose }: DeleteUpdateProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [deleteDocumentRecord, { loading: deleteDocumentRecordLoading }] = useMutation(STRATEGY_DETAILS_PAGE_DELETE_DOCUMENT_RECORD_MUTATION, {
    onError: (error) => {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  });

  const saveValue = () => {
    setErrorMessage(null);
    if (!currentRow) {
      setErrorMessage(defaultErrorMessage);
      return;
    }

    deleteDocumentRecord({
      variables: {
        id: currentRow.id
      },
      onCompleted: () => {
        listDocumentRecordsRefetch();
        onClose();
      }
    });
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{ name: 'Yes, Delete', onClick: saveValue, disabled: deleteDocumentRecordLoading }}
      isUseOutsideClick={!deleteDocumentRecordLoading}
      showCloseIcon={!deleteDocumentRecordLoading}
      showCancelButton={!deleteDocumentRecordLoading}
    >
      <ModalContainer>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {deleteDocumentRecordLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitel>Please confirm you want to delete this update.</ModalTitel>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>This update will be permanently deleted.</ModalDescription>
          </>
        )}
      </ModalContainer>
    </ModalWrappedContent>
  );
};

export default DeleteUpdate;

const ModalContainer = styled.div`
  font-weight: 400;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ModalTitel = styled.div`
  font-size: 33px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  line-height: 27px;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
